/**
 * CspPage — CSP（升學及職涯起航平台）product landing page.
 *
 * CSP is YADA's school-branded platform for further-study and career
 * navigation: a no-code, one-stop portal (Studies Hub, FuturePath
 * Odyssey, Featured Stories, mock interviews, CV clinic, mentorship)
 * that a school can switch on under its own name and domain.
 *
 * WHY THE COPY IS A SET OF CMS SLOTS
 * ----------------------------------
 * The page used to read every string through `t()`, so an operator could
 * not touch a word of it without a deploy. Its copy now lives in the CMS
 * as one slot per visual block (`src/admin/schemas/csp.ts`): the i18n
 * bundle still defines the SHAPE — how many cards, how many FAQ
 * entries, in which order — and a CMS override supplies only VALUES,
 * merged over that shape. Editing the page therefore cannot change its
 * layout.
 *
 * The FAQ is deliberately ONE slot: the visible `<details>` list and the
 * FAQPage JSON-LD read the same merged value, so the answers a visitor
 * reads and the answers the search engine reads can never disagree.
 *
 * The page used to be plain i18n, unlike Harbour / ValuesIncubator,
 * which read their body copy from `cms_product_pages`: CSP's copy is a
 * PRODUCT SHEET — a fixed set of feature cards, a stats row, an offer
 * banner and a set of marketing taglines that are reviewed as a unit.
 * A single free-text row could not keep those blocks in step with each
 * other; one slot per block can, because every block keeps its own
 * shape and the page reads all of them from the same merged values.
 *
 * VISUAL IDENTITY
 * ---------------
 * The page carries its own stylesheet (src/styles/csp.css), scoped
 * under `.csp-root` so the cream / green / gold palette reaches this
 * page and nothing else. The site navbar and footer around it keep
 * the YADA identity, while the page body is the CSP product sheet.
 * This mirrors the way the standalone BiteLearn landing page carries
 * its own CSS.
 *
 * The stylesheet is imported from src/main.tsx, NOT from here: the
 * prerender pass (scripts/prerender.tsx) renders this component in
 * plain Node via tsx, and Node cannot import a `.css` file — a
 * component-level CSS import fails the whole prerender with
 * `Unknown file extension ".css"`. main.tsx is the browser entry, so
 * Vite bundles the stylesheet there and the prerendered pages inherit
 * it through the same <link> the SPA shell uses.
 *
 * IMAGES
 * ------
 * Every image is referenced by its PUBLIC path, never imported from
 * src/assets — the prerender pass renders in plain Node and cannot
 * resolve asset imports. Each one carries an explicit width/height
 * (CLS) and `loading="lazy"` below the fold; the hero image is eager
 * because it is above the fold.
 */
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { SITE } from '@/content/site'
import { useSlotObject, useSlotT } from '@/cms/useSlotT'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildFAQPage } from '@/utils/structuredData'
import { buildLocalePath, parseLocaleFromPath } from '@/utils/localePath'

/** The CSP platform's own site — an external destination, always a plain <a>. */
const CSP_SITE_URL = 'https://csp.yada.hk'

/** Public asset paths (see the IMAGES note above). */
const IMG_HERO = '/products/csp/hero.jpg'
const IMG_LOGO = '/products/csp/csp-logo.png'
const IMG_FUTUREPATH = '/products/csp/futurepath-insert.jpg'
const IMG_STORIES = '/products/csp/stories-insert.jpg'
const IMG_QR = '/products/csp/qr-whatsapp.png'

interface StatItem {
  value: string
  label: string
}

interface FeatureItem {
  title: string
  text: string
}

interface StepItem {
  title: string
  text: string
}

interface FaqItem {
  question: string
  answer: string
}

/**
 * The block shapes, as the i18n bundles define them.
 *
 * Every one of these is the shape of ONE json slot (see
 * `src/admin/schemas/csp.ts`). The CMS override is merged onto the i18n
 * default — an object recursively, an array index-wise — so the shape
 * never changes: a plain local interface is safe, exactly as the
 * hand-written item interfaces above already are.
 */
interface CspHero {
  badge: string
  title: string
  titleEn: string
  lede: string
  chips: string[]
  ctaWhatsapp: string
  ctaSite: string
  imageAlt: string
  logoAlt: string
  badgeTitle: string
  badgeText: string
  whatsappText: string
}

interface CspStats {
  label: string
  items: StatItem[]
}

interface CspProblem {
  kicker: string
  title: string
  painBefore: string
  painStrong: string
  painAfter: string
  promiseHighlight: string
  promiseText: string
  imageAlt: string
}

interface CspSolution {
  kicker: string
  title: string
  intro: string
  checklist: string[]
}

interface CspFeatures {
  kicker: string
  title: string
  intro: string
  items: FeatureItem[]
}

interface CspCallout {
  name: string
  tag: string
  text: string
  imageAlt: string
}

interface CspCallouts {
  futurepath: CspCallout
  stories: CspCallout
}

interface CspStart {
  kicker: string
  title: string
  steps: StepItem[]
}

interface CspOffer {
  tag: string
  badgeBig: string
  badgeSub: string
  headline: string
  subline: string
  ctaWhatsapp: string
  ctaSite: string
  whatsappText: string
}

interface CspFaq {
  kicker: string
  title: string
  items: FaqItem[]
}

interface CspClosing {
  title: string
  text: string
  ctaWhatsapp: string
  ctaSite: string
  whatsappText: string
  qrAlt: string
}

interface CspBreadcrumb {
  home: string
  products: string
  self: string
}

/**
 * asText — the missing-string guard.
 *
 * i18next echoes the KEY back when a translation is missing, so a
 * missing string would otherwise reach the page as a dotted key path
 * ("csp.landing.hero.badge"). A value that still looks like one of
 * our namespaces is therefore treated as missing and renders as an
 * empty string. Same pattern as `asText()` in ProductDetailPage.
 *
 * It now wraps the SLOT result rather than `t()`: the slot resolver
 * falls back to the same i18n key, so the same guard still applies.
 */
function asText(value: unknown): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('csp.landing.') || trimmed.startsWith('seo.')) return ''
  return value
}

/**
 * asList — the missing-list guard.
 *
 * With `returnObjects: true` a missing key comes back as the key
 * STRING, not an array, so anything that is not an array is treated
 * as an empty list rather than a crash in `.map()`.
 *
 * It now wraps the SLOT result: the array inside a json slot is merged
 * onto the i18n default, which keeps the default's length, so the
 * number of cards on the page cannot change.
 */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export default function CspPage() {
  // `t` is kept for the one shared-chrome string at the bottom of the
  // page (`products.backToProducts`): six pages render it, so it must
  // stay plain i18n — one page must not own a key another page renders.
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const locale = parseLocaleFromPath(pathname)

  // The CMS slot resolvers. The locale is the ambient one (the page
  // renders in the language of the URL), the same one `t()` used.
  const slotObj = useSlotObject('csp')
  const slotT = useSlotT('csp')

  // ── Hero ──────────────────────────────────────────────────────────────
  const hero = slotObj('csp.landing.hero') as CspHero
  const heroBadge = asText(hero.badge)
  const heroTitle = asText(hero.title)
  const heroTitleEn = asText(hero.titleEn)
  const heroLede = asText(hero.lede)
  const heroChips = asList<string>(hero.chips)
  const heroCtaWhatsapp = asText(hero.ctaWhatsapp)
  const heroCtaSite = asText(hero.ctaSite)
  const heroImageAlt = asText(hero.imageAlt)
  const heroLogoAlt = asText(hero.logoAlt)
  const heroBadgeTitle = asText(hero.badgeTitle)
  const heroBadgeText = asText(hero.badgeText)
  const heroWhatsappText = asText(hero.whatsappText)

  // ── Stats ─────────────────────────────────────────────────────────────
  const stats = slotObj('csp.landing.stats') as CspStats
  const statsLabel = asText(stats.label)
  const statItems = asList<StatItem>(stats.items)

  // ── Problem ───────────────────────────────────────────────────────────
  const problem = slotObj('csp.landing.problem') as CspProblem
  const problemKicker = asText(problem.kicker)
  const problemTitle = asText(problem.title)
  const problemPainBefore = asText(problem.painBefore)
  const problemPainStrong = asText(problem.painStrong)
  const problemPainAfter = asText(problem.painAfter)
  const problemPromiseHighlight = asText(problem.promiseHighlight)
  const problemPromiseText = asText(problem.promiseText)
  const problemImageAlt = asText(problem.imageAlt)

  // ── Solution ──────────────────────────────────────────────────────────
  const solution = slotObj('csp.landing.solution') as CspSolution
  const solutionKicker = asText(solution.kicker)
  const solutionTitle = asText(solution.title)
  const solutionIntro = asText(solution.intro)
  const solutionChecklist = asList<string>(solution.checklist)

  // ── Features ──────────────────────────────────────────────────────────
  const features = slotObj('csp.landing.features') as CspFeatures
  const featuresKicker = asText(features.kicker)
  const featuresTitle = asText(features.title)
  const featuresIntro = asText(features.intro)
  const featureItems = asList<FeatureItem>(features.items)

  // ── Callouts ──────────────────────────────────────────────────────────
  const callouts = slotObj('csp.landing.callouts') as CspCallouts
  const futurepathName = asText(callouts.futurepath?.name)
  const futurepathTag = asText(callouts.futurepath?.tag)
  const futurepathText = asText(callouts.futurepath?.text)
  const futurepathImageAlt = asText(callouts.futurepath?.imageAlt)
  const storiesName = asText(callouts.stories?.name)
  const storiesTag = asText(callouts.stories?.tag)
  const storiesText = asText(callouts.stories?.text)
  const storiesImageAlt = asText(callouts.stories?.imageAlt)

  // ── How it starts ─────────────────────────────────────────────────────
  const start = slotObj('csp.landing.start') as CspStart
  const startKicker = asText(start.kicker)
  const startTitle = asText(start.title)
  const startSteps = asList<StepItem>(start.steps)

  // ── Offer ─────────────────────────────────────────────────────────────
  const offer = slotObj('csp.landing.offer') as CspOffer
  const offerTag = asText(offer.tag)
  const offerBadgeBig = asText(offer.badgeBig)
  const offerBadgeSub = asText(offer.badgeSub)
  const offerHeadline = asText(offer.headline)
  const offerSubline = asText(offer.subline)
  const offerCtaWhatsapp = asText(offer.ctaWhatsapp)
  const offerCtaSite = asText(offer.ctaSite)
  const offerWhatsappText = asText(offer.whatsappText)

  // ── FAQ ───────────────────────────────────────────────────────────────
  // ONE slot, TWO renderings: the `<details>` list below and the
  // FAQPage JSON-LD are both derived from `faqItems`, so an edit in the
  // CMS changes the visible answers and the structured data together.
  const faq = slotObj('csp.landing.faq') as CspFaq
  const faqKicker = asText(faq.kicker)
  const faqTitle = asText(faq.title)
  const faqItems = asList<FaqItem>(faq.items)

  // ── Closing CTA ───────────────────────────────────────────────────────
  const closing = slotObj('csp.landing.closing') as CspClosing
  const closingTitle = asText(closing.title)
  const closingText = asText(closing.text)
  const closingCtaWhatsapp = asText(closing.ctaWhatsapp)
  const closingCtaSite = asText(closing.ctaSite)
  const closingWhatsappText = asText(closing.whatsappText)
  const closingQrAlt = asText(closing.qrAlt)

  // ── Breadcrumb ────────────────────────────────────────────────────────
  const breadcrumb = slotObj('csp.landing.breadcrumb') as CspBreadcrumb
  const crumbHome = asText(breadcrumb.home)
  const crumbProducts = asText(breadcrumb.products)
  const crumbSelf = asText(breadcrumb.self)

  /**
   * WhatsApp deep link. The message text is per-locale (and differs
   * per CTA — the hero asks about the offer, the offer banner asks to
   * lock it in), so it is read from the bundle and URL-encoded here.
   */
  const whatsappHref = (message: string): string =>
    `${SITE.contact.whatsapp.link}?text=${encodeURIComponent(message)}`

  const faqSchema = faqItems
    .filter((item) => item.question && item.answer)
    .map((item) => ({ question: item.question, answer: item.answer }))

  return (
    <div className="csp-root csp-page">
      <SEO
        title={asText(slotT('seo.csp.title'))}
        description={asText(slotT('seo.csp.description'))}
        keywords={asText(slotT('seo.csp.keywords'))}
        jsonLd={[
          buildBreadcrumbList([
            { name: crumbHome, path: buildLocalePath('/', locale) },
            { name: crumbProducts, path: buildLocalePath('/products', locale) },
            { name: crumbSelf, path: buildLocalePath('/products/csp', locale) },
          ]),
          buildFAQPage(faqSchema),
        ]}
      />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="csp-hero" id="csp-top" aria-labelledby="csp-hero-title">
        <div className="csp-wrap csp-hero-grid">
          <div>
            <span className="csp-badge">
              <span className="csp-badge-dot" aria-hidden="true" />
              {heroBadge}
            </span>
            <h1 id="csp-hero-title">
              {heroTitle}
              <span className="csp-hero-en">{heroTitleEn}</span>
            </h1>
            <p className="csp-lede">{heroLede}</p>
            <div className="csp-chips">
              {heroChips.map((chip, index) => (
                <span
                  key={chip}
                  className={
                    index === heroChips.length - 1 ? 'csp-chip csp-chip-teal' : 'csp-chip'
                  }
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="csp-ctas">
              <a
                className="csp-btn csp-btn-primary"
                href={whatsappHref(heroWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {heroCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {heroCtaSite}
              </a>
            </div>
          </div>
          <div className="csp-hero-card">
            <span className="csp-sparkle csp-sparkle-tr" aria-hidden="true">
              ✦
            </span>
            <img
              src={IMG_HERO}
              alt={heroImageAlt}
              width={1280}
              height={720}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <div className="csp-float-badge">
              <img
                src={IMG_LOGO}
                alt={heroLogoAlt}
                width={352}
                height={352}
                loading="eager"
                decoding="async"
              />
              <div>
                <strong>{heroBadgeTitle}</strong>
                <span>{heroBadgeText}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────────────────── */}
      <div className="csp-wrap">
        <ul className="csp-stats" aria-label={statsLabel}>
          {statItems.map((item) => (
            <li className="csp-stat" key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Problem / why now ─────────────────────────────────────────── */}
      <section className="csp-section" id="csp-problem" aria-labelledby="csp-problem-title">
        <div className="csp-wrap csp-split">
          <div>
            <span className="csp-kicker">{problemKicker}</span>
            <h2 className="csp-section-title" id="csp-problem-title">{problemTitle}</h2>
            <div className="csp-pain-box">
              {problemPainBefore}
              <strong>{problemPainStrong}</strong>
              {problemPainAfter}
            </div>
            <div className="csp-promise-box">
              <span className="csp-hl">{problemPromiseHighlight}</span> {problemPromiseText}
            </div>
          </div>
          <figure className="csp-panel">
            <img
              src={IMG_HERO}
              alt={problemImageAlt}
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ── Solution overview ──────────────────────────────────────────── */}
      <section className="csp-section" id="csp-solution" aria-labelledby="csp-solution-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{solutionKicker}</span>
          <h2 className="csp-section-title" id="csp-solution-title">{solutionTitle}</h2>
          <p className="csp-intro">{solutionIntro}</p>
          <ul className="csp-checklist">
            {solutionChecklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Feature modules ────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-features" aria-labelledby="csp-features-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{featuresKicker}</span>
          <h2 className="csp-section-title" id="csp-features-title">{featuresTitle}</h2>
          <p className="csp-intro">{featuresIntro}</p>
          <div className="csp-features">
            {featureItems.map((item, index) => (
              <article className="csp-feature" key={item.title}>
                <div className="csp-feature-n" aria-hidden="true">
                  {index + 1}
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>

          <div className="csp-callouts">
            <article className="csp-callout">
              <div className="csp-callout-head">
                <span className="csp-callout-name">{futurepathName}</span>
                <span className="csp-callout-tag">{futurepathTag}</span>
              </div>
              <div className="csp-callout-body">
                <img
                  src={IMG_FUTUREPATH}
                  alt={futurepathImageAlt}
                  width={1100}
                  height={520}
                  loading="lazy"
                  decoding="async"
                />
                <p>{futurepathText}</p>
              </div>
            </article>
            <article className="csp-callout">
              <div className="csp-callout-head csp-callout-head-teal">
                <span className="csp-callout-name">{storiesName}</span>
                <span className="csp-callout-tag">{storiesTag}</span>
              </div>
              <div className="csp-callout-body">
                <img
                  src={IMG_STORIES}
                  alt={storiesImageAlt}
                  width={1100}
                  height={640}
                  loading="lazy"
                  decoding="async"
                />
                <p>{storiesText}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── How it starts ──────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-start" aria-labelledby="csp-start-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{startKicker}</span>
          <h2 className="csp-section-title" id="csp-start-title">{startTitle}</h2>
          <div className="csp-steps">
            {startSteps.map((item, index) => (
              <article className="csp-step" key={item.title}>
                <div className="csp-step-n" aria-hidden="true">
                  {index + 1}
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Offer band ─────────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-offer" aria-labelledby="csp-offer-title">
        <div className="csp-wrap">
          <div className="csp-offer">
            <div className="csp-offer-tag">{offerTag}</div>
            <div className="csp-offer-badge">
              <div className="csp-offer-big">{offerBadgeBig}</div>
              <div className="csp-offer-sub">{offerBadgeSub}</div>
            </div>
            <div>
              <h2 className="csp-offer-title" id="csp-offer-title">{offerHeadline}</h2>
              <div className="csp-offer-desc">{offerSubline}</div>
            </div>
            <div className="csp-offer-actions">
              <a
                className="csp-btn csp-btn-primary"
                href={whatsappHref(offerWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {offerCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {offerCtaSite}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-faq" aria-labelledby="csp-faq-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{faqKicker}</span>
          <h2 className="csp-section-title" id="csp-faq-title">{faqTitle}</h2>
          <div className="csp-faq">
            {faqItems.map((item, index) => (
              // The key is the INDEX plus the question, not the question
              // alone: an operator can type the same question twice in the
              // CMS, and two identical keys would make React reuse the wrong
              // <details> — the `open` flag would land on the wrong entry.
              // The index alone is unique, so a duplicate question can never
              // crash or mis-place an item.
              <details className="csp-faq-item" key={`${index}-${item.question}`} open={index === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-cta" aria-labelledby="csp-cta-title">
        <div className="csp-wrap">
          <div className="csp-closing">
            <h2 id="csp-cta-title">{closingTitle}</h2>
            <p>{closingText}</p>
            <div className="csp-ctas">
              <a
                className="csp-btn csp-btn-gold"
                href={whatsappHref(closingWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {closingCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {closingCtaSite}
              </a>
            </div>
            <div className="csp-qr">
              <img
                src={IMG_QR}
                alt={closingQrAlt}
                width={348}
                height={348}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Back to products ──────────────────────────────────────────── */}
      <div className="back-to-products text-center mt-12">
        <Link to={buildLocalePath('/products', locale)} className="csp-back">
          <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
          <span>{asText(t('products.backToProducts'))}</span>
        </Link>
      </div>
    </div>
  )
}
